import React, { useState, useMemo } from 'react';
import { X, Download, Eye, EyeOff, Edit3, Plus, Trash2, ChevronDown, ChevronUp, FileText, Filter, GripVertical } from 'lucide-react';
import { Button } from './Button';
import { buildPerformanceProofData, generatePerformanceProof, PerformanceProofData } from '../services/pdf/generatorPerformance';
import { buildActorName, realActorName } from '../services/actorName';
import { getRepresentatives } from '../services/projectRepresentatives';
import { useGesperrteKonten } from '../hooks/useGesperrteKonten';
import { fetchReportHistory, fetchReportHistoryWithChildren, saveProjectToCloud } from '../services/cloudService';
import { isHourlyUnit } from '../services/serviceUnits';
import { JournalMirrorRequest, mirrorEntriesToJournal } from '../services/serviceJournal';
import { bookingIsFlatProof, FLAT_PROOF_HINT, planFlatProofEntry } from '../services/flatRateLogic';
import { isFlatProofEntry } from '../services/serviceLogUtils';
import { ohnePaketZeilen, paketPositionIds } from './bestandteile/abschlussLogic';
import { Report, ServiceItem, ServiceTransaction } from '../types';
import type { ListProject } from '../services/listProject';
import { BUCHHALTUNG_NUR_LESEN_HINWEIS } from './invoices/rechnungRechte';

/**
 * Ein VOLL geladenes Projekt: AdminAccountingView holt es mit loadProjectById,
 * bevor der Editor aufgeht. Bewusst kein ListProject — der Export haengt
 * Handeintraege an und speichert `data` ueber saveProjectToCloud. Ein
 * Listenauszug wuerde dort abgewiesen und soll gar nicht erst hineinpassen.
 */
type ProofProject = Pick<ListProject, 'id'> & { data: Report };

interface Props {
    isOpen: boolean;
    onClose: () => void;
    project: ProofProject;
    userProfile: any;
    onRefresh?: () => void;
    /**
     * Lesemodus der Buchhaltung (ohne Recht „Rechnungen bearbeiten“): keine
     * neuen Einträge, und der Export schreibt nichts ins Projekt. Ausblenden,
     * Texte und Mengen für das PDF bleiben — die wirken nur dort.
     */
    nurLesen?: boolean;
}

export const PerformanceProofEditor: React.FC<Props> = ({ isOpen, onClose, project, userProfile, onRefresh, nurLesen = false }) => {
    const [dateStart, setDateStart] = useState<string>('');
    const [dateEnd, setDateEnd] = useState<string>('');
    const [isExporting, setIsExporting] = useState(false);
    const [expandedService, setExpandedService] = useState<string | null>(null);
    const [editingLogId, setEditingLogId] = useState<string | null>(null);
    const [editingDateLogId, setEditingDateLogId] = useState<string | null>(null);
    const [reportHistory, setReportHistory] = useState<any[]>([]);

    // Editable project name for PDF only (not saved to DB)
    const [pdfProjectName, setPdfProjectName] = useState<string>(
        project.data.metadata.projectName || ''
    );

    // Drag & Drop state for log entries
    const [draggingLogId, setDraggingLogId] = useState<string | null>(null);
    const [dragOverLogId, setDragOverLogId] = useState<string | null>(null);
    const [dragServiceId, setDragServiceId] = useState<string | null>(null);

    // Reorder logs within a service via drag & drop
    const handleLogDrop = (serviceId: string, targetLogId: string) => {
        if (!draggingLogId || draggingLogId === targetLogId || dragServiceId !== serviceId) {
            setDraggingLogId(null);
            setDragOverLogId(null);
            setDragServiceId(null);
            return;
        }
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => {
                if (s.id !== serviceId) return s;
                const logs = [...s.logs];
                const fromIdx = logs.findIndex(l => l.id === draggingLogId);
                const toIdx = logs.findIndex(l => l.id === targetLogId);
                if (fromIdx === -1 || toIdx === -1) return s;
                const [moved] = logs.splice(fromIdx, 1);
                logs.splice(toIdx, 0, moved);
                return { ...s, logs };
            })
        }));
        setDraggingLogId(null);
        setDragOverLogId(null);
        setDragServiceId(null);
    };

    // Build editable data
    const [proofData, setProofData] = useState<PerformanceProofData>(() =>
        buildPerformanceProofData(project, undefined, [])
    );

    const [, setHasLoadedHistory] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) {
            setHasLoadedHistory(false);
            setProofData({ services: [] });
            setDateStart('');
            setDateEnd('');
            return;
        }

        const loadHistories = async () => {
            if (!project?.id) return;

            try {
                // Own history plus child projects — shared loader, same source
                // the invoice annex uses (fetchReportHistoryWithChildren).
                const allHistories = await fetchReportHistoryWithChildren(project.id);

                setReportHistory(allHistories);
                setHasLoadedHistory(true);
                setProofData(buildPerformanceProofData(project, undefined, allHistories));
                setPdfProjectName(project.data.metadata.projectName || '');
            } catch (e) {
                console.error("Error loading histories for performance proof:", e);
                // Fallback to minimal
                const h = await fetchReportHistory(project.id);
                setReportHistory(h);
                setHasLoadedHistory(true);
                setProofData(buildPerformanceProofData(project, undefined, h));
                setPdfProjectName(project.data.metadata.projectName || '');
            }
        };

        loadHistories();
    }, [isOpen, project?.id]);

    // PDF column visibility — default OFF (Muster-PDF has no quantity columns)
    const [pdfColumns, setPdfColumns] = useState({
        contract: false,
        performed: false,
        billed: false,
        open: false
    });

    // Rebuild when date range changes — preserve custom visibility settings and manual entries
    const handleDateRangeChange = (start: string, end: string) => {
        setDateStart(start);
        setDateEnd(end);
        const range = (start || end) ? { start: start || null, end: end || null } : undefined;
        
        const newData = buildPerformanceProofData(project, range, reportHistory);
        // Get FULL data to correctly distinguish between project logs and manual editor logs
        const fullData = buildPerformanceProofData(project, undefined, reportHistory);
        
        // Preserve service and log visibility AND manual additions from current state
        const preservedServices = newData.services.map(s => {
            const existingS = proofData.services.find(curr => curr.id === s.id);
            const fullS = fullData.services.find(fs => fs.id === s.id);

            if (existingS && fullS) {
                // 1. Identify "local-only" logs (manual entries)
                // Check against ALL standard logs across ALL services (not just this one):
                // ein History-/Pauschale-Log, das in einem anderen Inspection-Service (z.B.
                // Tagespauschale vs. Halbtagespauschale) auftaucht, darf hier NICHT als
                // "manuell" gewertet und erneut eingemischt werden — sonst Doppel-Eintrag.
                const standardLogIds = new Set(fullData.services.flatMap(fs => fs.logs.map(l => l.id)));
                const manualLogs = existingS.logs.filter(l => !standardLogIds.has(l.id));
                
                // 2. Filter manual logs by the new date range as well
                const filteredManualLogs = manualLogs.filter(l => {
                    const logDate = (l.date || '').split('T')[0];
                    if (start && logDate < start) return false;
                    if (end && logDate > end) return false;
                    return true;
                });

                // 3. Merge logs — dedupe by id across both sources, damit derselbe
                // History-/Pauschale-Eintrag nicht doppelt erscheint.
                const seenLogIds = new Set<string>();
                const combinedLogs = [...s.logs, ...filteredManualLogs]
                    .filter(l => { if (seenLogIds.has(l.id)) return false; seenLogIds.add(l.id); return true; })
                    .map(l => {
                    const existingL = existingS.logs.find(currL => currL.id === l.id);
                    // Preserve visibility and any text changes from existing state
                    // (Unless the date changed via the date range filter, in which case s.logs has the right defaults)
                    return existingL ? { 
                        ...l, 
                        visible: existingL.visible,
                        description: existingL.description,
                        date: existingL.date,
                        quantity: existingL.quantity
                    } : l;
                });

                // 4. Recalculate quantityUsed for the service view 
                // (Sum of s.quantityUsed from newData + any visible manual logs matching range)
                const manualQty = filteredManualLogs.filter(l => l.visible).reduce((acc, l) => acc + (l.quantity || 0), 0);

                return { 
                    ...s, 
                    visible: existingS.visible,
                    quantityUsed: s.quantityUsed + manualQty,
                    quantityBilled: s.quantityBilled, // Use calculated from newData
                    logs: combinedLogs
                };
            }
            return s;
        });

        setProofData({ ...newData, services: preservedServices });
    };

    // Toggle service visibility
    const toggleService = (serviceId: string) => {
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => s.id === serviceId ? { ...s, visible: !s.visible } : s)
        }));
    };

    // Toggle log entry visibility
    const toggleLog = (serviceId: string, logId: string) => {
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => s.id === serviceId ? {
                ...s,
                logs: s.logs.map(l => l.id === logId ? { ...l, visible: !l.visible } : l)
            } : s)
        }));
    };

    // Edit log description
    const updateLogDescription = (serviceId: string, logId: string, newDesc: string) => {
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => s.id === serviceId ? {
                ...s,
                logs: s.logs.map(l => l.id === logId ? { ...l, description: newDesc } : l)
            } : s)
        }));
    };

    // Edit log date
    const updateLogDate = (serviceId: string, logId: string, newDate: string) => {
        if (!newDate) return;
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => s.id === serviceId ? {
                ...s,
                logs: s.logs.map(l => l.id === logId ? { ...l, date: newDate } : l)
            } : s)
        }));
        setEditingDateLogId(null);
    };

    /**
     * Durchgefuehrt von — nur bei MANUELLEN Eintraegen aenderbar.
     *
     * Ein manueller Eintrag traegt eine Leistung nach, die kein Bericht belegt:
     * die Begehung hat stattgefunden, es wurde nur nichts geschrieben und nichts
     * gebucht (Projekt 6172, 06.08.2026). Vorbelegt ist der eingeloggte Nutzer —
     * das ist aber gerade NICHT zwingend der, der vor Ort war, und im
     * Leistungsnachweis steht der Name beim Kunden. Deshalb waehlbar.
     *
     * Gebuchte Zeilen und Zeilen aus dem Archiv bleiben unangetastet: ihr Name
     * kommt aus Buchung bzw. `sent_by`, und der Editor speichert sie ohnehin
     * nicht zurueck (nur `isManual`-Zeilen werden geschrieben).
     */
    const updateLogPerformer = (serviceId: string, logId: string, newName: string) => {
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => s.id === serviceId ? {
                ...s,
                logs: s.logs.map(l => l.id === logId ? { ...l, performerName: newName } : l)
            } : s)
        }));
    };

    /**
     * Zur Auswahl stehen die Verantwortlichen des Projekts (Koordinator und
     * Vertreter) und der eingeloggte Nutzer. Ein bereits eingetragener Name
     * bleibt in der Liste, auch wenn die Person heute nicht mehr zugeordnet ist
     * — sonst verschwaende die Auswahl den Namen beim ersten Klick.
     * Gesperrte Mitglieder (Nr. 148) werden nicht mehr angeboten; ein schon
     * eingetragener Name bleibt aus demselben Grund stehen.
     */
    const gesperrteKonten = useGesperrteKonten();
    const performerOptions = useMemo(() => {
        const namen = getRepresentatives(project?.data?.metadata)
            .filter(r => !r.id || !gesperrteKonten.has(r.id))
            .map(r => realActorName(r.name))
            .filter(Boolean);
        const eigener = buildActorName(userProfile);
        return Array.from(new Set([...namen, eigener].filter(Boolean)));
    }, [project, userProfile, gesperrteKonten]);

    /**
     * Kennung zum gewaehlten Namen. Ohne sie stuende in der Buchung die id des
     * BEDIENERS unter dem Namen eines anderen — genau die Verwechslung, an der
     * die Vertreter-Telefonnummern haengengeblieben sind. Ist die Person nicht
     * zuzuordnen (freier Altbestand), bleibt die Kennung leer: kein Ausweis ist
     * besser als ein falscher.
     */
    const performerIdByName = useMemo(() => {
        const map = new Map<string, string>();
        getRepresentatives(project?.data?.metadata).forEach(r => {
            const name = realActorName(r.name);
            if (name && r.id) map.set(name, r.id);
        });
        const eigener = buildActorName(userProfile);
        if (eigener && userProfile?.id) map.set(eigener, userProfile.id);
        return map;
    }, [project, userProfile]);

    /**
     * Die Pauschalen des Projekts: auf ihnen ist jeder Handeintrag ein
     * NACHWEIS (Menge 0, NW-…) und kein Zaehlerstand. Frueher zaehlte der
     * Editor auch hier +1 und schrieb beim Export quantityUsed der Pauschale
     * hoch — ein Monat Geld ohne FLAT-Schluessel.
     */
    const flatServices = useMemo(() => {
        const map = new Map<string, ServiceItem>();
        ((project?.data?.metadata?.globalData?.services || []) as ServiceItem[]).forEach(s => {
            if (s?.id && bookingIsFlatProof(s)) map.set(s.id, s);
        });
        return map;
    }, [project]);

    /**
     * Die Paketpositionen (Plan Bestandteile, Kernregel 7): auf ihnen gibt es
     * KEINE Handzeile — ein Eintrag hier schriebe beim Export Menge auf ein
     * Paket, waehrend Bestandteile offen sind. Ein Paket bucht nur der
     * Abschlussschritt bzw. der Dialog der Bestandteile.
     */
    const paketServices = useMemo(
        () => paketPositionIds((project?.data?.metadata?.globalData?.services || []) as ServiceItem[]),
        [project],
    );

    // Delete log entry
    const deleteLog = (serviceId: string, logId: string) => {
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => s.id === serviceId ? {
                ...s,
                logs: s.logs.filter(l => l.id !== logId)
            } : s)
        }));
    };

    // Add manual log entry — also increments quantityUsed (ausser auf einer
    // Pauschale: dort ist der Eintrag ein Nachweis ohne Menge).
    const addManualLog = (serviceId: string) => {
        if (nurLesen) return;
        // Kein Handeintrag auf einer Paketposition (Knopf ist dort auch aus).
        if (paketServices.has(serviceId)) return;
        const today = new Date().toISOString().split('T')[0];
        const nachweis = flatServices.has(serviceId);
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => s.id === serviceId ? {
                ...s,
                quantityUsed: nachweis ? s.quantityUsed : s.quantityUsed + 1,
                logs: [...s.logs, {
                    id: crypto.randomUUID(),
                    date: today,
                    description: 'Manueller Eintrag',
                    quantity: nachweis ? 0 : 1,
                    // Kein Truemmerstueck und kein 'System' im Kundendokument:
                    // buildActorName liefert '' statt "Max undefined" oder
                    // " ", und der Nachweis laesst die Spalte dann leer.
                    performerName: buildActorName(userProfile),
                    visible: true,
                    isManual: true,
                    // Der Druck stellt die Zeile als Nachweis unter ihren Monat.
                    ...(nachweis ? { flatProof: true } : {}),
                }]
            } : s)
        }));
    };

    // Update service quantities
    const updateServiceQuantity = (serviceId: string, field: 'quantityUsed' | 'quantityBilled', value: number) => {
        setProofData(prev => ({
            ...prev,
            services: prev.services.map(s => s.id === serviceId ? { ...s, [field]: value } : s)
        }));
    };

    // Stats
    const stats = useMemo(() => {
        const visible = proofData.services.filter(s => s.visible);
        const hidden = proofData.services.length - visible.length;
        const totalLogs = visible.reduce((a, s) => a + s.logs.filter(l => l.visible).length, 0);
        return { visible: visible.length, hidden, totalLogs };
    }, [proofData]);

    // Export PDF + save NEW manual entries to DB (never delete or modify existing)
    const handleExport = async () => {
        setIsExporting(true);
        // Die Zweitschrift ins Buchungsjournal (S4b) haengt bewusst HINTER dem
        // try: dessen catch meldet dem Anwender einen Exportfehler, und ein
        // Journal, das nur die zweite Schrift fuehrt, darf nie eine Meldung zu
        // einer Buchung ausloesen, die laengst gespeichert ist. Vorgemerkt wird
        // erst nach dem bestaetigten `saveProjectToCloud`.
        let spiegelung: JournalMirrorRequest | null = null;
        try {
            const meta = project.data.metadata;
            const existingLog = meta.globalData?.serviceLog || [];
            const existingServices = meta.globalData?.services || [];

            // --- DB: Only APPEND new manual entries. Never delete or modify existing. ---
            const existingLogIds = new Set(existingLog.map((e: any) => e.id));
            // Legacy log entries can carry date = null/invalid — normalize instead
            // of crashing (".includes" on null) and fall back to today.
            const toIsoDate = (d: any): string => {
                if (typeof d === 'string' && d.includes('T')) return d;
                const parsed = d ? new Date(d) : null;
                return parsed && !isNaN(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString();
            };
            // CRITICAL: only persist entries the user explicitly created in this
            // editor (isManual). The proof data also contains SYNTHETIC log rows
            // reconstructed from the report history (id = history id, not in the
            // serviceLog) — previously those were treated as "new" and written to
            // the DB on every PDF export, silently incrementing quantityUsed.
            const newLogEntries: any[] = [];
            proofData.services.forEach(s => {
                s.logs.forEach(l => {
                    if ((l as any).isManual === true && !existingLogIds.has(l.id)) {
                        // Kennung und Name muessen dieselbe Person meinen.
                        const performerId = performerIdByName.get(realActorName(l.performerName)) || '';
                        const performerName = realActorName(l.performerName) || buildActorName(userProfile);
                        const pauschale = flatServices.get(s.id);
                        if (pauschale) {
                            const nachweis: ServiceTransaction = planFlatProofEntry({
                                id: l.id,
                                flatServiceId: s.id,
                                date: toIsoDate(l.date),
                                bookedAt: new Date().toISOString(),
                                performerId,
                                performerName,
                                description: l.description,
                            });
                            newLogEntries.push(nachweis);
                            return;
                        }
                        newLogEntries.push({
                            id: l.id,
                            date: toIsoDate(l.date),
                            serviceId: s.id,
                            quantity: l.quantity,
                            performerId,
                            performerName,
                            isSubstitution: false,
                            description: l.description
                        });
                    }
                });
            });

            // Zweite Sperre: eine Handzeile auf einer Paketposition geht nie in
            // die Datenbank (Kernregel 7) — auch nicht, wenn sie anders als
            // ueber den Knopf in den Entwurf kam.
            const zulaessig = ohnePaketZeilen(newLogEntries, existingServices);
            newLogEntries.length = 0;
            newLogEntries.push(...zulaessig);

            // Only write to DB if there are actually new entries — und nie im
            // Lesemodus: dort entsteht kein neuer Eintrag, die Bedingung ist
            // die zweite Sicherung.
            if (newLogEntries.length > 0 && !nurLesen) {
                const finalLog = [...existingLog, ...newLogEntries];

                // Update quantityUsed for services that got new entries
                const newEntriesPerService = new Map<string, number>();
                newLogEntries.forEach(e => {
                    // Eine Nachweiszeile fasst den Zaehler der Pauschale nie an.
                    if (isFlatProofEntry(e)) return;
                    newEntriesPerService.set(e.serviceId, (newEntriesPerService.get(e.serviceId) || 0) + e.quantity);
                });

                const updatedServices = existingServices.map((s: any) => {
                    const addedQty = newEntriesPerService.get(s.id);
                    if (addedQty) return { ...s, quantityUsed: (s.quantityUsed || 0) + addedQty };
                    return s;
                });

                const updatedProject = {
                    ...project.data,
                    metadata: {
                        ...meta,
                        globalData: {
                            ...meta.globalData,
                            services: updatedServices,
                            serviceLog: finalLog
                        }
                    }
                };

                await saveProjectToCloud(updatedProject, project.id);
                // Keine `statements`: Beschreibung und Menge stammen aus den
                // Handeintraegen dieses Editors und sagen nichts Sicheres
                // darueber, ob eine Begehung gedeckt ist. Erschliessen darf es
                // die Abbildung, raten darf es diese Stelle nicht — auch fuer
                // Nachweiszeilen: inferInspectionCoverage kennt sie und ist mit
                // der SQL-Nachspiegelung (Nr. 134) abgeglichen (Codex R2).
                spiegelung = {
                    projectId: project.id,
                    entries: newLogEntries,
                    previousLog: existingLog,
                    source: 'performance_proof',
                    linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
                };
            }

            // --- PDF: Use editor data with pdfProjectName and column config ---
            const dataWithConfig: PerformanceProofData = {
                ...proofData,
                // Take the range straight from the inputs — proofData.dateRange can
                // be stale if the rebuild after a range change failed or was skipped.
                dateRange: (dateStart || dateEnd) ? { start: dateStart || null, end: dateEnd || null } : proofData.dateRange,
                pdfColumns,
                pdfProjectName: pdfProjectName || meta.projectName
            };
            const doc = await generatePerformanceProof(project, userProfile, dataWithConfig);
            
            // Use pdfProjectName for filename too
            const safeName = (pdfProjectName || meta.projectNumber || 'Projekt').replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_');
            const filename = `Leistungsnachweis_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(filename);

            if (onRefresh) onRefresh();

        } catch (e) {
            console.error(e);
            alert('Fehler beim Exportieren.');
        } finally {
            setIsExporting(false);
        }

        // Auch wenn die PDF-Erzeugung oben gescheitert ist: die Eintraege
        // stehen dann bereits im JSON und gehoeren damit ins Journal.
        if (spiegelung) await mirrorEntriesToJournal(spiegelung);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="font-bold text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-blue-400" /> Leistungsnachweis bearbeiten</h3>
                        <p className="text-slate-400 text-xs mt-0.5">#{project.data.metadata.projectNumber}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span className="bg-slate-800 px-2 py-1 rounded">{stats.visible} Positionen</span>
                            {stats.hidden > 0 && <span className="bg-amber-900/50 text-amber-400 px-2 py-1 rounded">{stats.hidden} ausgeblendet</span>}
                            <span className="bg-slate-800 px-2 py-1 rounded">{stats.totalLogs} Einträge</span>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
                    </div>
                </div>

                {/* PDF Project Name + Date Range Filter */}
                <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 space-y-2">
                    {/* Editable PDF Project Name */}
                    <div className="flex items-center gap-3">
                        <label className="text-xs font-bold text-slate-500 shrink-0 w-24">PDF-Projektname:</label>
                        <input
                            type="text"
                            value={pdfProjectName}
                            onChange={e => setPdfProjectName(e.target.value)}
                            className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            placeholder="Projektname auf der PDF..."
                        />
                        {pdfProjectName !== project.data.metadata.projectName && (
                            <button
                                onClick={() => setPdfProjectName(project.data.metadata.projectName || '')}
                                className="text-[11px] text-slate-400 hover:text-blue-600 font-bold"
                            >
                                Zurücksetzen
                            </button>
                        )}
                    </div>

                    {/* Date Range + Column Toggles */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Filter className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-bold text-slate-500">Zeitraum:</span>
                        <input type="date" value={dateStart} onChange={e => handleDateRangeChange(e.target.value, dateEnd)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-blue-400" />
                        <span className="text-xs text-slate-400">bis</span>
                        <input type="date" value={dateEnd} onChange={e => handleDateRangeChange(dateStart, e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-blue-400" />
                        {(dateStart || dateEnd) && (
                            <button onClick={() => handleDateRangeChange('', '')} className="text-[11px] text-blue-600 font-bold hover:underline">Zurücksetzen</button>
                        )}
                        <div className="flex-1" />

                        {/* PDF Column Toggles */}
                        <div className="flex items-center gap-1 border-l border-slate-200 pl-3">
                            <span className="text-[11px] font-bold text-slate-400 mr-1">Spalten:</span>
                            {([
                                { key: 'contract', label: 'Vertrag' },
                                { key: 'performed', label: 'Erbracht' },
                                { key: 'billed', label: 'Abgerechnet' },
                                { key: 'open', label: 'Offen' }
                            ] as const).map(col => (
                                <button
                                    key={col.key}
                                    onClick={() => setPdfColumns(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                                    className={`text-[11px] font-bold px-2 py-0.5 rounded border transition-colors ${
                                        pdfColumns[col.key]
                                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                                            : 'bg-white text-slate-400 border-slate-200 hover:text-slate-600'
                                    }`}
                                >
                                    {col.label}
                                </button>
                            ))}
                        </div>

                        <button onClick={() => setProofData(prev => ({ ...prev, services: prev.services.map(s => ({ ...s, visible: true })) }))} className="text-[11px] font-bold text-slate-500 hover:text-blue-600 px-2 py-1 border border-slate-200 rounded-lg">Alle einblenden</button>
                    </div>
                </div>

                {/* Service List */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
                    {proofData.services.map(service => {
                        const isExpanded = expandedService === service.id;
                        // KERNREGEL der Monatspauschale, wie im PDF darunter
                        // (generatorPerformance): eine gedeckte Position hat kein
                        // offenes Geld — der Editor zeigte sonst ein "offen",
                        // das der Export gleich danach zu Recht weglaesst.
                        const open = service.flatCovered ? 0 : Math.max(0, service.quantityUsed - service.quantityBilled);
                        // Ueber isHourlyUnit: die eigene Pruefung kannte nur 'Stunden',
                        // eine 'Std'-Position blieb hier ohne Kurzform.
                        const unitShort = isHourlyUnit(service.unit) ? 'Std.' : service.unit.toLowerCase().includes('stück') ? 'Stk.' : service.unit;
                        const visibleLogCount = service.logs.filter(l => l.visible).length;

                        return (
                            <div key={service.id} className={`border rounded-xl overflow-hidden transition-all ${service.visible ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-50'}`}>
                                {/* Service Header */}
                                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedService(isExpanded ? null : service.id)}>
                                    <button onClick={e => { e.stopPropagation(); toggleService(service.id); }} className={`p-1 rounded ${service.visible ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-300 hover:bg-slate-100'}`} title={service.visible ? 'Ausblenden' : 'Einblenden'}>
                                        {service.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    </button>

                                    <span className="font-mono text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">{service.lvPosition}</span>
                                    <span className="text-sm font-bold text-slate-700 truncate flex-1">
                                        {service.title}
                                        {service.flatCovered && (
                                            <span className="ml-2 text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">
                                                in der Monatspauschale enthalten
                                            </span>
                                        )}
                                    </span>

                                    <div className="flex items-center gap-3 text-xs shrink-0">
                                        <span className="text-slate-400">{service.isNep ? 'NEP' : `${service.quantityContracted} ${unitShort}`}</span>
                                        <span className="font-bold text-slate-700">{service.quantityUsed} erbr.</span>
                                        <span className="text-emerald-600 font-bold">{service.quantityBilled} abger.</span>
                                        {open > 0 && <span className="text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded">{open} offen</span>}
                                        <span className="text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{visibleLogCount} Eintr.</span>
                                    </div>

                                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                </div>

                                {/* Expanded: Logs + Edit */}
                                {isExpanded && service.visible && (
                                    <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-3">
                                        {/* Editable quantities */}
                                        <div className="flex items-center gap-4 bg-slate-50 p-3 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <label className="text-[11px] font-bold text-slate-400 uppercase">Erbracht:</label>
                                                <input type="number" value={service.quantityUsed} onChange={e => updateServiceQuantity(service.id, 'quantityUsed', parseFloat(e.target.value) || 0)} className="w-16 text-center text-sm font-bold border border-slate-200 rounded-lg px-2 py-1 outline-none focus:border-blue-400" />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="text-[11px] font-bold text-slate-400 uppercase">Abgerechnet:</label>
                                                <input type="number" value={service.quantityBilled} onChange={e => updateServiceQuantity(service.id, 'quantityBilled', parseFloat(e.target.value) || 0)} className="w-16 text-center text-sm font-bold border border-slate-200 rounded-lg px-2 py-1 outline-none focus:border-emerald-400" />
                                            </div>
                                            <div className="flex-1" />
                                            {paketServices.has(service.id) ? (
                                                <span className="text-[11px] text-slate-500" title="Paketposition mit Bestandteilen — sie wird gebucht, sobald alle Bestandteile erledigt sind.">
                                                    Paketposition — kein Handeintrag
                                                </span>
                                            ) : (
                                                <button onClick={() => addManualLog(service.id)} disabled={nurLesen} title={nurLesen ? BUCHHALTUNG_NUR_LESEN_HINWEIS : undefined} className="text-[11px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-lg border border-blue-200 flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent">
                                                    <Plus className="w-3 h-3" /> Eintrag hinzufügen
                                                </button>
                                            )}
                                        </div>
                                        {flatServices.has(service.id) && (
                                            <p className="text-[11px] text-indigo-700 font-medium px-1">{FLAT_PROOF_HINT}</p>
                                        )}

                                        {/* Log entries */}
                                        <div className="space-y-1">
                                            {service.logs.length === 0 ? (
                                                <p className="text-xs text-slate-400 italic py-2 text-center">Keine Einträge</p>
                                            ) : service.logs.map((log) => (
                                                <div
                                                    key={log.id}
                                                    draggable
                                                    onDragStart={(e) => {
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        setDraggingLogId(log.id);
                                                        setDragServiceId(service.id);
                                                    }}
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        e.dataTransfer.dropEffect = 'move';
                                                        if (dragOverLogId !== log.id) setDragOverLogId(log.id);
                                                    }}
                                                    onDragLeave={() => { if (dragOverLogId === log.id) setDragOverLogId(null); }}
                                                    onDrop={(e) => { e.preventDefault(); handleLogDrop(service.id, log.id); }}
                                                    onDragEnd={() => { setDraggingLogId(null); setDragOverLogId(null); setDragServiceId(null); }}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all
                                                        ${draggingLogId === log.id ? 'opacity-30 scale-95' : ''}
                                                        ${dragOverLogId === log.id && draggingLogId !== log.id ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-200' : ''}
                                                        ${log.visible ? 'border-slate-100 bg-white' : 'border-slate-100 bg-slate-50 opacity-40'}
                                                    `}
                                                >
                                                    <div className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0">
                                                        <GripVertical className="w-3 h-3" />
                                                    </div>

                                                    <button onClick={() => toggleLog(service.id, log.id)} className={`p-0.5 rounded ${log.visible ? 'text-emerald-500' : 'text-slate-300'}`}>
                                                        {log.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                                    </button>

                                                    {editingDateLogId === log.id ? (
                                                        <input
                                                            type="date"
                                                            value={(log.date || '').split('T')[0]}
                                                            onChange={e => updateLogDate(service.id, log.id, e.target.value)}
                                                            onBlur={() => setEditingDateLogId(null)}
                                                            className="font-mono text-xs shrink-0 w-[105px] border border-blue-300 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-blue-400"
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <span
                                                            className="font-mono text-slate-500 shrink-0 w-16 cursor-pointer hover:text-blue-600 hover:underline"
                                                            onClick={() => setEditingDateLogId(log.id)}
                                                            title="Datum ändern"
                                                        >
                                                            {new Date(log.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                                        </span>
                                                    )}

                                                    {editingLogId === log.id ? (
                                                        <input
                                                            type="text"
                                                            value={log.description}
                                                            onChange={e => updateLogDescription(service.id, log.id, e.target.value)}
                                                            onBlur={() => setEditingLogId(null)}
                                                            onKeyDown={e => e.key === 'Enter' && setEditingLogId(null)}
                                                            className="flex-1 text-xs border border-blue-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-400"
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <span className="flex-1 text-slate-700 truncate cursor-pointer hover:text-blue-600" onClick={() => setEditingLogId(log.id)}>{log.description || '—'}</span>
                                                    )}

                                                    {/* Storniert: derselbe Vermerk, den das PDF druckt — die
                                                        Vorschau darf nicht harmloser aussehen als das Dokument. */}
                                                    {log.waived && (
                                                        <span
                                                            className="shrink-0 text-[10px] uppercase font-bold tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"
                                                            title="Auf diese Leistung wurde verzichtet — sie steht im Nachweis, wird aber nicht berechnet."
                                                        >
                                                            storniert
                                                        </span>
                                                    )}

                                                    {/* Durchgefuehrt von — waehlbar, solange die Zeile manuell ist */}
                                                    {(log as any).isManual ? (
                                                        <select
                                                            value={log.performerName || ''}
                                                            onChange={e => updateLogPerformer(service.id, log.id, e.target.value)}
                                                            title="Durchgeführt von"
                                                            className="text-[11px] shrink-0 max-w-[130px] border border-slate-200 rounded px-1 py-0.5 outline-none focus:border-blue-400 bg-white text-slate-600"
                                                        >
                                                            <option value="">— Durchgeführt von —</option>
                                                            {Array.from(new Set([
                                                                ...performerOptions,
                                                                ...(log.performerName ? [log.performerName] : []),
                                                            ])).map(name => (
                                                                <option key={name} value={name}>{name}</option>
                                                            ))}
                                                        </select>
                                                    ) : log.performerName ? (
                                                        <span className="text-slate-400 text-[11px] shrink-0 max-w-[100px] truncate" title={log.performerName}>{log.performerName}</span>
                                                    ) : null}

                                                    {/* Nachweiszeile: keine Menge — „×0" laese sich wie ein Fehler.
                                                        „enthalten" wie im Druck (generatorPerformance). */}
                                                    <span className="text-slate-400 shrink-0">{log.flatProof ? 'enthalten' : `×${log.quantity}`}</span>

                                                    <button onClick={() => setEditingLogId(log.id)} className="p-0.5 text-slate-300 hover:text-blue-600"><Edit3 className="w-3 h-3" /></button>
                                                    <button onClick={() => deleteLog(service.id, log.id)} className="p-0.5 text-slate-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center shrink-0">
                    <div className="text-xs text-slate-400">
                        {nurLesen
                            ? 'Nur lesen: gespeichert wird nichts. Ausblenden, Textänderungen und Mengen wirken nur auf die PDF.'
                            : 'Neue Einträge werden gespeichert. Ausblenden, Textänderungen und Mengen wirken nur auf die PDF.'}
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
                        <Button variant="primary" onClick={handleExport} disabled={isExporting} icon={<Download className="w-4 h-4" />}>
                            {isExporting ? 'Exportiert...' : 'PDF exportieren'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};